/**
 * Compact Invoices list — Phase 15.6.
 *
 * Columns (44 px sticky-header rows):
 *   ☐ | Invoice# | Client | Trip ID | Date | Destination
 *     | Taxable | GST (amber if >0) | TCS (amber if >0)
 *     | Total (green=paid, red=overdue, amber=partial)
 *     | Status pill | Actions (hover-only)
 *
 * Status pills: Paid=green, Overdue=red, Partial=amber, Unpaid=light red, Draft=gray
 * Filter chips: All | Overdue | Unpaid | Partial | Paid
 * Totals row above pagination: Sum of Taxable / GST / TCS / Total
 * Export CSV uses GST-register column order (Month, Invoice#, PAN,
 * Taxable, GST, TCS, Total) so the CA can paste it directly.
 *
 * Member-expression-free in JSX to dodge the Babel plugin recursion.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Search, Eye, MessageCircle, Download as DownloadIcon, Plus, Pencil, Repeat, Ban, FilePlus2, X, IndianRupee, Loader2, Calculator, Trash2 } from 'lucide-react';
import { generatePDF } from '@/utils/generatePDF';
import WhatsAppSendDialog from '@/components/whatsapp/WhatsAppSendDialog';
import { waNumber } from '@/utils/phone';
import { useAuth } from '@/contexts/AuthContext';
import FilterChips from '@/components/list/FilterChips';
import BulkBar from '@/components/list/BulkBar';
import Pagination from '@/components/list/Pagination';
import ExportButton from '@/components/list/ExportButton';
import SortableHeader from '@/components/list/SortableHeader';
import ManualInvoiceDialog from '@/components/invoice/ManualInvoiceDialog';
import FromTripInvoiceDialog from '@/components/invoice/FromTripInvoiceDialog';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const fmtINR = (n) => '\u20B9' + Number(n || 0).toLocaleString('en-IN', { maximumFractionDigits: 0 });
const fmtDate = (s) => {
  if (!s) return '—';
  try { return new Date(s).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: '2-digit' }); }
  catch { return '—'; }
};
const fmtMonth = (s) => {
  if (!s) return '';
  try { return new Date(s).toLocaleDateString('en-IN', { month: 'short', year: 'numeric' }); }
  catch { return ''; }
};

// Computed status — invoices have a `status` field but it doesn't capture
// part-payment state vs. overdue. We derive a richer status here.
const computeStatus = (inv) => {
  const total = Number(inv.totalAmount || 0);
  const paid = Number(inv.amountPaid || 0);  // populated only when receipts logged
  const explicit = (inv.status || '').toLowerCase();
  if (explicit === 'cancelled' || explicit === 'void' || explicit === 'voided') return 'Cancelled';
  if (explicit === 'converted') return 'Converted';
  if (explicit === 'draft') return 'Draft';
  if (explicit === 'paid' || (total > 0 && paid >= total)) return 'Paid';
  const due = inv.dueDate ? new Date(inv.dueDate) : null;
  const overdue = due && due < new Date() && paid < total;
  if (overdue) return 'Overdue';
  if (paid > 0 && paid < total) return 'Partial';
  return 'Unpaid';
};

const STATUS_PILL = {
  Paid:      'bg-emerald-50 text-emerald-700 border-emerald-200',
  Overdue:   'bg-rose-50 text-rose-700 border-rose-200',
  Partial:   'bg-amber-50 text-amber-700 border-amber-200',
  Unpaid:    'bg-rose-50 text-rose-500 border-rose-100',
  Draft:     'bg-slate-100 text-slate-600 border-slate-200',
  Cancelled: 'bg-slate-100 text-slate-500 border-slate-200 line-through',
  Converted: 'bg-violet-50 text-violet-700 border-violet-200',
};

const TOTAL_COLOR = {
  Paid:      'text-emerald-700',
  Overdue:   'text-rose-700',
  Partial:   'text-amber-700',
  Unpaid:    'text-slate-700',
  Draft:     'text-slate-500',
  Cancelled: 'text-slate-400',
  Converted: 'text-slate-500',
};

const InvoicesList = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const tripFilter = searchParams.get('tripId') || '';
  const statusParam = (searchParams.get('status') || '').toLowerCase();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState(statusParam === 'outstanding' ? 'Outstanding' : 'All');
  const [selected, setSelected] = useState(new Set());
  const [sort, setSort] = useState({ key: 'invoiceDate', dir: 'desc' });
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  // Phase 16.2 — New Invoice dialog. Opens on the "+ New invoice" button
  // and also auto-opens when `?new=1` is in the URL (so the QuickActions
  // tile and the legacy `/invoices/new` route both work).
  const [showNewInvoice, setShowNewInvoice] = useState(() => searchParams.get('new') === '1');
  // Trip-linked invoice generation (captures tripId + displayTripId + posts
  // the receivable to the ledger). Opened locked-to-trip from a trip's
  // "Invoice" deep-link (?tripId=…), or with a trip picker via "From trip".
  const [showFromTrip, setShowFromTrip] = useState(false);
  // F#1 — invoice being edited (opens ManualInvoiceDialog pre-filled → PUT).
  const [editingInvoice, setEditingInvoice] = useState(null);
  const [converting, setConverting] = useState(null);
  // Record Payment — one-click receipt logging from the list (posts to the
  // invoice + the traveller ledger so receivables reconcile in real time).
  const [payingInvoice, setPayingInvoice] = useState(null);

  const fetchInvoices = useCallback(() => {
    setLoading(true);
    const url = tripFilter
      ? `${BACKEND_URL}/api/invoices?trip_id=${encodeURIComponent(tripFilter)}`
      : `${BACKEND_URL}/api/invoices`;
    axios.get(url, { withCredentials: true })
      .then((res) => setRows(Array.isArray(res.data) ? res.data : (res.data?.items || [])))
      .catch(() => toast.error('Failed to load invoices'))
      .finally(() => setLoading(false));
  }, [tripFilter]);

  useEffect(() => { fetchInvoices(); }, [fetchInvoices]);

  // Open the New Invoice dialog if `?new=1` arrives later (e.g. QuickActions
  // tile navigation while already on the page).
  useEffect(() => {
    if (searchParams.get('new') === '1') setShowNewInvoice(true);
  }, [searchParams]);

  // Sync the status filter when arriving via a deep-link like
  // /finance/invoices?status=outstanding (Dashboard "Payments Due" card).
  useEffect(() => {
    if (statusParam === 'outstanding') setStatusFilter('Outstanding');
  }, [statusParam]);

  // When the dialog closes, drop the `new=1` query param so a reload
  // doesn't reopen it.
  const closeNewInvoice = (open) => {
    setShowNewInvoice(open);
    if (!open && searchParams.get('new') === '1') {
      searchParams.delete('new');
      setSearchParams(searchParams, { replace: true });
    }
  };

  const clearTripFilter = () => {
    searchParams.delete('tripId');
    setSearchParams(searchParams, { replace: true });
  };

  // Convert a proforma → tax invoice (server clones + marks the proforma
  // 'converted'). Mirrors the legacy /invoices/legacy behaviour.
  const handleConvert = async (inv) => {
    const newNum = window.prompt('Enter the new tax invoice number (or leave blank for auto):', '');
    if (newNum === null) return;
    setConverting(inv.invoiceNumber);
    try {
      const res = await axios.post(
        `${BACKEND_URL}/api/invoices/${encodeURIComponent(inv.invoiceNumber)}/convert`,
        { invoiceNumber: newNum.trim() || undefined },
        { withCredentials: true },
      );
      toast.success(`Tax invoice ${res.data.invoiceNumber} created from proforma`);
      fetchInvoices();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Conversion failed');
    } finally {
      setConverting(null);
    }
  };

  // Soft-cancel — keeps the record but flips status to 'cancelled', which the
  // backend cascades by reversing the linked invoice_issued ledger entry.
  const handleCancel = async (num) => {
    if (!window.confirm('Cancel this invoice?\nIt stays on record but is marked Cancelled and its ledger entry is reversed.')) return;
    try {
      await axios.put(
        `${BACKEND_URL}/api/invoices/${encodeURIComponent(num)}`,
        { status: 'cancelled' },
        { withCredentials: true },
      );
      toast.success('Invoice cancelled');
      fetchInvoices();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to cancel invoice');
    }
  };

  useEffect(() => { setSelected(new Set()); setPage(1); }, [statusFilter, search, sort, pageSize]);

  // Annotate computed status once; cheaper than recomputing in every cell.
  const annotated = useMemo(() => rows.map((r) => ({ ...r, _status: computeStatus(r) })), [rows]);

  const counts = useMemo(() => {
    const c = { All: annotated.length, Outstanding: 0, Overdue: 0, Unpaid: 0, Partial: 0, Paid: 0 };
    for (const r of annotated) {
      if (c[r._status] !== undefined) c[r._status] += 1;
      if (r._status === 'Overdue' || r._status === 'Unpaid' || r._status === 'Partial') c.Outstanding += 1;
    }
    return c;
  }, [annotated]);

  const filtered = useMemo(() => {
    let out = annotated;
    if (statusFilter === 'Outstanding') {
      out = out.filter((r) => r._status === 'Overdue' || r._status === 'Unpaid' || r._status === 'Partial');
    } else if (statusFilter !== 'All') {
      out = out.filter((r) => r._status === statusFilter);
    }
    if (search) {
      const q = search.toLowerCase();
      out = out.filter((r) =>
        (r.invoiceNumber || '').toLowerCase().includes(q) ||
        ((r.client && r.client.name) || '').toLowerCase().includes(q) ||
        ((r.client && r.client.displayUCC) || '').toLowerCase().includes(q) ||
        ((r.client && r.client.ucc) || '').toLowerCase().includes(q) ||
        (r.clientUCC || '').toLowerCase().includes(q) ||
        (r.tripId || '').toLowerCase().includes(q) ||
        (r.displayTripId || '').toLowerCase().includes(q),
      );
    }
    return out;
  }, [annotated, statusFilter, search]);

  const sorted = useMemo(() => {
    if (!sort.key) return filtered;
    const dir = sort.dir === 'asc' ? 1 : -1;
    const get = (r) => {
      if (sort.key === 'client') return ((r.client && r.client.name) || '').toLowerCase();
      if (sort.key === 'taxable') return Number(r.baseCost || 0);
      if (sort.key === 'gst') return Number(r.gstAmount || 0);
      if (sort.key === 'tcs') return Number(r.tcsAmount || 0);
      if (sort.key === 'total') return Number(r.totalAmount || 0);
      return r[sort.key] || '';
    };
    return [...filtered].sort((a, b) => (get(a) < get(b) ? -1 : get(a) > get(b) ? 1 : 0) * dir);
  }, [filtered, sort]);

  // Rows that count toward money totals/reports — cancelled/void + converted
  // invoices are excluded everywhere (totals AND the CA CSV export) so a voided
  // invoice never forms part of any calculation.
  const calcRows = useMemo(
    () => sorted.filter((r) => r._status !== 'Cancelled' && r._status !== 'Converted'),
    [sorted],
  );

  const totals = useMemo(() => calcRows.reduce((acc, r) => ({
    taxable: acc.taxable + Number(r.baseCost || 0),
    gst: acc.gst + Number(r.gstAmount || 0),
    tcs: acc.tcs + Number(r.tcsAmount || 0),
    total: acc.total + Number(r.totalAmount || 0),
  }), { taxable: 0, gst: 0, tcs: 0, total: 0 }), [calcRows]);

  const pageRows = useMemo(() => sorted.slice((page - 1) * pageSize, page * pageSize), [sorted, page, pageSize]);

  const chips = [
    { value: 'All',         label: 'All',         count: counts.All },
    { value: 'Outstanding', label: 'Outstanding', count: counts.Outstanding },
    { value: 'Overdue',     label: 'Overdue',     count: counts.Overdue },
    { value: 'Unpaid',      label: 'Unpaid',      count: counts.Unpaid },
    { value: 'Partial',     label: 'Partial',     count: counts.Partial },
    { value: 'Paid',        label: 'Paid',        count: counts.Paid },
  ];

  // GST-register column order — CA-friendly export.
  const exportColumns = [
    { key: 'month', label: 'Month', format: (r) => fmtMonth(r.invoiceDate) },
    { key: 'invoiceNumber', label: 'Invoice #' },
    { key: 'pan', label: 'PAN', format: (r) => (r.client && r.client.panCard) || '' },
    { key: 'taxable', label: 'Taxable', format: (r) => Number(r.baseCost || 0) },
    { key: 'gst', label: 'GST', format: (r) => Number(r.gstAmount || 0) },
    { key: 'tcs', label: 'TCS', format: (r) => Number(r.tcsAmount || 0) },
    { key: 'total', label: 'Total', format: (r) => Number(r.totalAmount || 0) },
  ];

  const toggle = (id) => {
    setSelected((s) => {
      const n = new Set(s);
      if (n.has(id)) n.delete(id); else n.add(id);
      return n;
    });
  };
  const toggleAll = () => {
    if (selected.size === pageRows.length) setSelected(new Set());
    else setSelected(new Set(pageRows.map((r) => r.invoiceNumber)));
  };

  const tripLabel = tripFilter
    ? ((rows[0] && (rows[0].displayTripId || rows[0].tripId)) || tripFilter)
    : '';

  return (
    <div className="space-y-4" data-testid="invoices-page">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-2xl font-semibold text-slate-900">Invoices</h1>
          <p className="text-sm text-slate-500 mt-0.5">{loading ? 'Loading…' : `${rows.length} ${tripFilter ? 'for this trip' : 'on file'}`}</p>
        </div>
        <div className="flex items-center gap-2">
          {tripFilter ? (
            <Button
              onClick={() => setShowFromTrip(true)}
              className="bg-emerald-700 hover:bg-emerald-800 text-white"
              size="sm"
              data-testid="new-trip-invoice-btn"
            >
              <Plus className="h-4 w-4 mr-1" /> New invoice for this trip
            </Button>
          ) : (
            <>
              <Button
                onClick={() => setShowFromTrip(true)}
                variant="outline"
                size="sm"
                className="border-teal-600 text-teal-700 hover:bg-teal-50"
                data-testid="from-trip-invoice-btn"
              >
                <FilePlus2 className="h-4 w-4 mr-1" /> From trip
              </Button>
              <Button
                onClick={() => setShowNewInvoice(true)}
                className="bg-emerald-700 hover:bg-emerald-800 text-white"
                size="sm"
                data-testid="new-invoice-btn"
              >
                <Plus className="h-4 w-4 mr-1" /> New invoice
              </Button>
            </>
          )}
          <ExportButton filename="invoices" columns={exportColumns} rows={calcRows} testid="invoices-export" />
        </div>
      </div>

      {tripFilter ? (
        <div className="flex items-center gap-2 text-xs bg-teal-50 border border-teal-200 rounded-lg px-3 py-2" data-testid="trip-filter-banner">
          <span className="text-slate-600">Showing invoices for trip</span>
          <span className="font-mono font-semibold text-teal-800">{tripLabel}</span>
          <button onClick={clearTripFilter} className="ml-auto inline-flex items-center gap-1 text-slate-500 hover:text-rose-600" data-testid="clear-trip-filter">
            <X className="h-3.5 w-3.5" /> View all invoices
          </button>
        </div>
      ) : null}

      <div className="flex items-center gap-3 flex-wrap">
        <div className="relative" style={{ width: 280 }}>
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-slate-400" />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search invoice#, client, trip…"
            className="pl-8 h-9 rounded-[7px]"
            data-testid="invoices-search"
          />
        </div>
        <FilterChips chips={chips} value={statusFilter} onChange={setStatusFilter} testidPrefix="status-chip" />
      </div>

      <BulkBar
        count={selected.size}
        actions={[
          { key: 'remind', label: 'WhatsApp reminder' },
          { key: 'export', label: 'Export selected' },
        ]}
        onAction={(k) => {
          if (k === 'remind') toast.info('Bulk reminder — coming soon');
          if (k === 'export') toast.info('Use the Export button at top right (already respects filters).');
        }}
        onClear={() => setSelected(new Set())}
        testid="invoices-bulk-bar"
      />

      <InvoicesTable
        pageRows={pageRows}
        loading={loading}
        sort={sort} setSort={setSort}
        selected={selected} toggle={toggle} toggleAll={toggleAll}
        onEdit={setEditingInvoice}
        onConvert={handleConvert}
        onCancel={handleCancel}
        onRecordPayment={setPayingInvoice}
        onRecalculated={fetchInvoices}
        converting={converting}
      />

      <TotalsRow totals={totals} />

      <Pagination
        page={page} pageSize={pageSize} total={sorted.length}
        onPageChange={setPage} onPageSizeChange={setPageSize}
        testid="invoices-pagination"
      />

      {/* New invoice — reuses the same ManualInvoiceDialog from /invoices/legacy
          (Phase 16.2). Opens via the toolbar button, the QuickActions tile, or
          the legacy /invoices/new URL redirect. */}
      <ManualInvoiceDialog
        open={showNewInvoice}
        onOpenChange={closeNewInvoice}
        onCreated={(inv) => {
          closeNewInvoice(false);
          toast.success(`Invoice ${inv?.invoiceNumber || ''} created`);
          fetchInvoices();
        }}
      />

      {/* Trip-linked generation — captures tripId + displayTripId and posts
          the receivable to the ledger. Locked to the trip when arriving from
          a trip's "Invoice" deep-link (?tripId=…). */}
      <FromTripInvoiceDialog
        open={showFromTrip}
        onOpenChange={setShowFromTrip}
        presetTripId={tripFilter || undefined}
        onCreated={() => { setShowFromTrip(false); fetchInvoices(); }}
      />

      {/* F#1 EDIT — same ManualInvoiceDialog reused in edit mode (PUT). */}
      <ManualInvoiceDialog
        open={!!editingInvoice}
        onOpenChange={(o) => { if (!o) setEditingInvoice(null); }}
        editing={editingInvoice}
        onCreated={(inv) => {
          setEditingInvoice(null);
          toast.success(`Invoice ${inv?.invoiceNumber || ''} updated`);
          fetchInvoices();
        }}
      />

      {/* Record Payment — one-click receipt logging without leaving the list. */}
      <RecordPaymentDialog
        invoice={payingInvoice}
        onClose={() => setPayingInvoice(null)}
        onRecorded={() => { setPayingInvoice(null); fetchInvoices(); }}
        onChanged={fetchInvoices}
      />
    </div>
  );
};

const InvoicesTable = ({ pageRows, loading, sort, setSort, selected, toggle, toggleAll, onEdit, onConvert, onCancel, onRecordPayment, onRecalculated, converting }) => {
  const allChecked = pageRows.length > 0 && pageRows.every((r) => selected.has(r.invoiceNumber));
  return (
    <div className="border border-slate-200 rounded-xl overflow-hidden bg-white" data-testid="invoices-table">
      <div className="overflow-x-auto">
        <table className="w-full text-xs">
          <thead className="bg-slate-50 sticky top-0 z-10">
            <tr className="text-left">
              <th className="w-10 px-3 py-2">
                <input type="checkbox" checked={allChecked} onChange={toggleAll} data-testid="inv-check-all" />
              </th>
              <th className="px-3 py-2"><SortableHeader label="Invoice #" columnKey="invoiceNumber" sort={sort} setSort={setSort} /></th>
              <th className="px-3 py-2"><SortableHeader label="Client" columnKey="client" sort={sort} setSort={setSort} /></th>
              <th className="px-3 py-2 text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Trip ID</th>
              <th className="px-3 py-2"><SortableHeader label="Date" columnKey="invoiceDate" sort={sort} setSort={setSort} /></th>
              <th className="px-3 py-2 text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Destination</th>
              <th className="px-3 py-2 text-right"><SortableHeader label="Taxable" columnKey="taxable" sort={sort} setSort={setSort} align="right" /></th>
              <th className="px-3 py-2 text-right"><SortableHeader label="GST" columnKey="gst" sort={sort} setSort={setSort} align="right" /></th>
              <th className="px-3 py-2 text-right"><SortableHeader label="TCS" columnKey="tcs" sort={sort} setSort={setSort} align="right" /></th>
              <th className="px-3 py-2 text-right"><SortableHeader label="Total" columnKey="total" sort={sort} setSort={setSort} align="right" /></th>
              <th className="px-3 py-2 text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Status</th>
              <th className="px-3 py-2 text-[10px] uppercase tracking-wider text-slate-500 font-semibold text-right">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {loading ? (
              <tr><td colSpan={12} className="py-8 text-center text-slate-400 text-xs">Loading…</td></tr>
            ) : pageRows.length === 0 ? (
              <tr><td colSpan={12} className="py-8 text-center text-slate-400 text-xs">No invoices match the current filters.</td></tr>
            ) : pageRows.map((r) => (
              <InvoiceRow
                key={r.invoiceNumber}
                r={r}
                checked={selected.has(r.invoiceNumber)}
                toggle={toggle}
                onEdit={onEdit}
                onConvert={onConvert}
                onCancel={onCancel}
                onRecordPayment={onRecordPayment}
                onRecalculated={onRecalculated}
                converting={converting}
              />
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

const InvoiceRow = ({ r, checked, toggle, onEdit, onConvert, onCancel, onRecordPayment, onRecalculated, converting }) => {
  const status = r._status;
  const gst = Number(r.gstAmount || 0);
  const tcs = Number(r.tcsAmount || 0);
  const totalClass = TOTAL_COLOR[status] || 'text-slate-700';
  const clientName = (r.client && r.client.name) || '—';
  const initials = clientName.split(' ').map((p) => p[0]).join('').slice(0, 2).toUpperCase();
  const phone = (r.client && r.client.phone) || '';
  const waUrl = phone ? `https://wa.me/${waNumber(r.client?.countryCode, phone)}?text=${encodeURIComponent('Invoice ' + r.invoiceNumber)}` : null;
  const dest = (r.items && r.items[0] && r.items[0].destination) || r.destination || '—';
  const tripRef = r.displayTripId || r.tripId || '—';

  return (
    <tr className="group hover:bg-slate-50" style={{ height: 44 }} data-testid={`invoice-row-${r.invoiceNumber}`}>
      <td className="px-3 py-1.5">
        <input type="checkbox" checked={checked} onChange={() => toggle(r.invoiceNumber)} data-testid={`inv-check-${r.invoiceNumber}`} />
      </td>
      <td className="px-3 py-1.5 font-mono text-[11px] text-slate-900 font-semibold">{r.invoiceNumber}</td>
      <td className="px-3 py-1.5">
        <div className="flex items-center gap-2">
          <div className="h-7 w-7 rounded-full bg-emerald-100 text-emerald-700 text-[10px] font-semibold flex items-center justify-center">{initials}</div>
          <span className="truncate max-w-[160px]">{clientName}</span>
        </div>
      </td>
      <td className="px-3 py-1.5 font-mono text-[11px] text-slate-600">{tripRef}</td>
      <td className="px-3 py-1.5 text-slate-600">{fmtDate(r.invoiceDate)}</td>
      <td className="px-3 py-1.5 text-slate-600">{dest}</td>
      <td className="px-3 py-1.5 text-right font-mono">{fmtINR(r.baseCost)}</td>
      <td className={`px-3 py-1.5 text-right font-mono ${gst > 0 ? 'text-amber-700' : 'text-slate-400'}`}>{gst > 0 ? fmtINR(gst) : '—'}</td>
      <td className={`px-3 py-1.5 text-right font-mono ${tcs > 0 ? 'text-amber-700' : 'text-slate-400'}`}>{tcs > 0 ? fmtINR(tcs) : '—'}</td>
      <td className={`px-3 py-1.5 text-right font-mono font-semibold ${totalClass}`}>
        {fmtINR(r.totalAmount)}
        {status === 'Partial' ? (
          <div className="text-[9px] font-normal text-slate-400 mt-0.5" data-testid={`paid-due-${r.invoiceNumber}`}>
            {fmtINR(r.amountPaid)} paid · {fmtINR(Math.max(0, Number(r.totalAmount || 0) - Number(r.amountPaid || 0)))} due
          </div>
        ) : null}
      </td>
      <td className="px-3 py-1.5">
        <span className={`inline-flex items-center text-[10px] font-medium border rounded-full px-2 py-0.5 ${STATUS_PILL[status]}`} data-testid={`status-${r.invoiceNumber}`}>
          {status}
        </span>
      </td>
      <td className="px-3 py-1.5 text-right">
        <InvoiceActions r={r} waUrl={waUrl} onEdit={onEdit} onConvert={onConvert} onCancel={onCancel} onRecordPayment={onRecordPayment} onRecalculated={onRecalculated} converting={converting} />
      </td>
    </tr>
  );
};

const InvoiceActions = ({ r, waUrl, onEdit, onConvert, onCancel, onRecordPayment, onRecalculated, converting }) => {
  const invoiceNumber = r.invoiceNumber;
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const status = (r.status || '').toLowerCase();
  const isProforma = (r.invoiceType || '').toLowerCase() === 'proforma';
  const canConvert = isProforma && status !== 'converted' && status !== 'cancelled';
  const canCancel = status !== 'cancelled' && status !== 'converted';
  // FIX 4 (Task 5) — hide Record Payment when there's nothing left to collect
  // (outstanding = 0, incl. fully-paid and ₹0 invoices) or it's cancelled/converted.
  const outstanding = Math.max(0, Number(r.totalAmount || 0) - Number(r.amountPaid || 0));
  const canRecordPayment = status !== 'cancelled' && status !== 'converted' && outstanding > 0;
  const isConverting = converting === invoiceNumber;
  // FIX 1 — the old <a href="/api/invoices/{n}/pdf"> 404'd (no such endpoint)
  // AND left invoice-number slashes unencoded. Use the same proven Playwright
  // download flow as the in-app preview (generatePDF → /api/pdf/render).
  const [downloading, setDownloading] = useState(false);
  // FIX 1 — admin-only manual recalculation. Old invoices created before an
  // engine change keep stale gst/tcs/totals; reapplying the current engine
  // refreshes them in-place. Manual only — never auto-overwrites.
  const [recalculating, setRecalculating] = useState(false);
  const [waSendOpen, setWaSendOpen] = useState(false);
  const handleRecalculate = async () => {
    if (!window.confirm(`Recalculate invoice ${invoiceNumber}? This reapplies the current tax engine and overwrites the stored GST/TCS/Total.`)) return;
    setRecalculating(true);
    try {
      await axios.post(`${BACKEND_URL}/api/invoices/${encodeURIComponent(invoiceNumber)}/recalculate`, {}, { withCredentials: true });
      toast.success(`Invoice ${invoiceNumber} recalculated`);
      onRecalculated?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Recalculation failed');
    } finally {
      setRecalculating(false);
    }
  };
  const handleDownload = async () => {
    setDownloading(true);
    try {
      const safeRef = String(invoiceNumber || '').replace(/[^a-zA-Z0-9-_]/g, '-');
      const stamp = r.updatedAt || '';
      await generatePDF({
        pageUrl: `${window.location.origin}/invoice-preview/${encodeURIComponent(invoiceNumber)}`,
        filename: `${safeRef}-Invoice.pdf`,
        waitUntil: 'load',
        waitSelector: '[data-testid="invoice-preview-page"]',
        cacheKey: stamp ? `invoice:${invoiceNumber}:${stamp}` : null,
      });
      toast.success('PDF downloaded');
    } catch (e) {
      toast.error('PDF generation failed');
    } finally {
      setDownloading(false);
    }
  };
  return (
    <div className="inline-flex items-center gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity">
      <ActionBtn title="Download PDF" testid={`inv-pdf-${invoiceNumber}`} disabled={downloading} onClick={handleDownload}>
        {downloading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <DownloadIcon className="h-3.5 w-3.5" />}
      </ActionBtn>
      {waUrl ? (
        <a href={waUrl} target="_blank" rel="noopener noreferrer" data-testid={`inv-wa-${invoiceNumber}`}>
          <ActionBtn title="WhatsApp"><MessageCircle className="h-3.5 w-3.5" /></ActionBtn>
        </a>
      ) : null}
      <ActionBtn title="WhatsApp (official)" tone="success" testid={`inv-wa-cloud-${invoiceNumber}`} onClick={() => setWaSendOpen(true)}>
        <MessageCircle className="h-3.5 w-3.5" />
      </ActionBtn>
      {waSendOpen && (
        <WhatsAppSendDialog open={waSendOpen} onOpenChange={setWaSendOpen} contextType="invoice" contextId={invoiceNumber} title={r?.client?.guestName || r?.guestName} />
      )}
      <Link to={`/invoice-preview/${encodeURIComponent(invoiceNumber)}`} data-testid={`inv-view-${invoiceNumber}`}>
        <ActionBtn title="View"><Eye className="h-3.5 w-3.5" /></ActionBtn>
      </Link>
      {canRecordPayment ? (
        <ActionBtn title="Record payment" tone="success" testid={`record-payment-${invoiceNumber}`} onClick={() => onRecordPayment(r)}>
          <IndianRupee className="h-3.5 w-3.5" />
        </ActionBtn>
      ) : null}
      <ActionBtn title="Edit" testid={`edit-invoice-${invoiceNumber}`} onClick={() => onEdit(r)}>
        <Pencil className="h-3.5 w-3.5" />
      </ActionBtn>
      {canConvert ? (
        <ActionBtn title="Convert to Tax invoice" tone="warn" disabled={isConverting} testid={`convert-invoice-${invoiceNumber}`} onClick={() => onConvert(r)}>
          <Repeat className="h-3.5 w-3.5" />
        </ActionBtn>
      ) : null}
      {canCancel ? (
        <ActionBtn title="Cancel invoice" tone="warn" testid={`cancel-invoice-${invoiceNumber}`} onClick={() => onCancel(invoiceNumber)}>
          <Ban className="h-3.5 w-3.5" />
        </ActionBtn>
      ) : null}
      {isAdmin ? (
        <ActionBtn title="Recalculate (admin)" disabled={recalculating} testid={`recalculate-invoice-${invoiceNumber}`} onClick={handleRecalculate}>
          {recalculating ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Calculator className="h-3.5 w-3.5" />}
        </ActionBtn>
      ) : null}
    </div>
  );
};

const ActionBtn = ({ children, title, tone, onClick, testid, disabled }) => (
  <button
    type="button"
    title={title}
    onClick={onClick}
    disabled={disabled}
    data-testid={testid}
    className={`h-[26px] w-[26px] inline-flex items-center justify-center rounded-[6px] transition-colors ${
      tone === 'danger' ? 'text-rose-600 hover:bg-rose-50'
        : tone === 'warn' ? 'text-amber-700 hover:bg-amber-50'
        : tone === 'success' ? 'text-emerald-700 hover:bg-emerald-50'
        : 'text-slate-600 hover:bg-slate-100'
    } ${disabled ? 'opacity-40 cursor-not-allowed' : ''}`}
  >
    {children}
  </button>
);

/**
 * One recorded payment inside RecordPaymentDialog. Extracted into its own
 * component for the same reason as InvoiceRow — the visual-edits Babel plugin
 * blows its stack on deeply nested inline JSX blocks.
 */
const PaymentRow = ({ payment, canRemove, removing, onRemove }) => (
  <div className="flex items-center gap-2 px-3 py-2" data-testid={`payment-row-${payment.paymentId}`}>
    <div className="min-w-0 flex-1">
      <div className="flex items-center gap-1.5 flex-wrap">
        <span className="font-mono text-xs font-semibold text-slate-800">{fmtINR(payment.amount)}</span>
        <span className="text-[11px] text-slate-500">{payment.method || 'other'}</span>
        {payment.date ? <span className="text-[11px] text-slate-400">{payment.date}</span> : null}
        {payment.receiptId ? (
          <span className="text-[10px] px-1.5 py-0.5 rounded border border-teal-200 bg-teal-50 text-teal-700" data-testid="payment-row-linked">
            trip receipt
          </span>
        ) : null}
      </div>
      {payment.note ? <p className="text-[11px] text-slate-400 truncate">{payment.note}</p> : null}
    </div>
    {canRemove ? (
      <button
        type="button"
        disabled={removing}
        onClick={() => onRemove(payment)}
        title="Remove this payment"
        className="shrink-0 text-slate-400 hover:text-rose-600 disabled:opacity-40"
        data-testid={`remove-payment-${payment.paymentId}`}
      >
        {removing ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Trash2 className="h-3.5 w-3.5" />}
      </button>
    ) : null}
  </div>
);

const RecordPaymentDialog = ({ invoice, onClose, onRecorded, onChanged }) => {
  const { user } = useAuth();
  // Mirrors the backend guard on DELETE /api/invoices/{n}/payment/{id}
  // (require_accounts = admin + accounts). Everyone else still sees the
  // recorded payments, they just get no Remove control.
  const canRemovePayment = ['admin', 'accounts'].includes((user?.role || '').toLowerCase());
  const open = !!invoice;
  // Removing a payment changes amountPaid, so the dialog reads its money
  // position from the delete response once one exists rather than from the
  // now-stale list row — otherwise Outstanding and the over-payment guard in
  // submit() below would both keep using pre-removal figures.
  const [live, setLive] = useState(null);
  const current = live || invoice;
  const total = Number(current?.totalAmount || 0);
  const paid = Number(current?.amountPaid || 0);
  const outstanding = Math.max(0, Math.round((total - paid) * 100) / 100);
  const [amount, setAmount] = useState('');
  const [method, setMethod] = useState('upi');
  const [date, setDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [note, setNote] = useState('');
  const [saving, setSaving] = useState(false);
  // BATCH 2 follow-up (dedupe guard) — optionally settle an EXISTING trip
  // receipt instead of recording a second money-in entry (which would
  // double-count). We load the trip's receipts and mark those already linked.
  const [receipts, setReceipts] = useState([]);
  const [linkReceiptId, setLinkReceiptId] = useState('');
  // Payments already recorded against this invoice. GET /api/invoices already
  // returns `payments`, so this is seeded from the row and then kept in local
  // state so a removal refreshes the list without closing the dialog.
  const [payments, setPayments] = useState([]);
  const [removingId, setRemovingId] = useState('');
  const [accountId, setAccountId] = useState('');
  const [bankAccounts, setBankAccounts] = useState([]);

  useEffect(() => {
    axios.get(`${BACKEND_URL}/api/accounting/bank-accounts`, { withCredentials: true })
      .then((r) => setBankAccounts(r.data || []))
      .catch(() => {});
  }, []);
  // Bumped after a removal so the trip-receipt dropdown re-loads: removing a
  // receipt-linked payment frees that receipt to be linked again.
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    if (!invoice || !invoice.tripId) { setReceipts([]); setLinkReceiptId(''); return; }
    let alive = true;
    setLinkReceiptId('');
    Promise.all([
      axios.get(`${BACKEND_URL}/api/trips/${encodeURIComponent(invoice.tripId)}`, { withCredentials: true }).then((r) => r.data).catch(() => null),
      axios.get(`${BACKEND_URL}/api/invoices?trip_id=${encodeURIComponent(invoice.tripId)}`, { withCredentials: true }).then((r) => (Array.isArray(r.data) ? r.data : [])).catch(() => []),
    ]).then(([trip, invs]) => {
      if (!alive) return;
      const linked = new Set();
      (invs || []).forEach((iv) => (iv.payments || []).forEach((p) => { if (p.receiptId) linked.add(p.receiptId); }));
      const avail = ((trip && trip.receipts) || []).filter((r) => r.receiptId && !linked.has(r.receiptId));
      setReceipts(avail);
    });
    return () => { alive = false; };
  }, [invoice, reloadKey]);

  const selectedReceipt = receipts.find((r) => r.receiptId === linkReceiptId) || null;

  // Seed the amount with the outstanding balance each time a new invoice opens.
  useEffect(() => {
    // Drop any previous invoice's post-removal snapshot so figures never leak
    // between rows (the dialog stays mounted between opens).
    setLive(null);
    if (invoice) {
      const t = Number(invoice.totalAmount || 0);
      const p = Number(invoice.amountPaid || 0);
      setAmount(String(Math.max(0, Math.round((t - p) * 100) / 100)));
      setMethod('upi');
      setDate(new Date().toISOString().slice(0, 10));
      setNote('');
      setAccountId('');
      setPayments(Array.isArray(invoice.payments) ? invoice.payments : []);
    } else {
      setPayments([]);
    }
  }, [invoice]);

  // Reverse a wrongly-entered payment via the existing
  // DELETE /api/invoices/{n}/payment/{id}. Confirm-guarded because the backend
  // archives-then-deletes and cascades the cashbook row — it is not a soft undo.
  const handleRemovePayment = async (p) => {
    if (!invoice || !p || !p.paymentId) return;
    const consequence = p.receiptId
      ? 'This payment settles a trip receipt. Removing it only unlinks the receipt — the receipt, and the money it recorded, stay on the trip.'
      : 'This also reverses the cashbook entry this payment posted.';
    const when = p.date ? ` dated ${p.date}` : '';
    if (!window.confirm(
      `Remove the ${fmtINR(p.amount)} payment${when} from invoice ${invoice.invoiceNumber}?\n\n`
      + `${consequence}\n\nThe entry is archived for audit, but this cannot be undone from the UI.`,
    )) return;
    setRemovingId(p.paymentId);
    try {
      const res = await axios.delete(
        `${BACKEND_URL}/api/invoices/${encodeURIComponent(invoice.invoiceNumber)}/payment/${encodeURIComponent(p.paymentId)}`,
        { withCredentials: true },
      );
      const updated = res.data || {};
      setLive(updated);
      setPayments(Array.isArray(updated.payments) ? updated.payments : []);
      setReloadKey((k) => k + 1);
      toast.success(`Payment of ${fmtINR(p.amount)} removed`);
      // Same refresh the Recalculate action uses — repaints the row's paid
      // amount and status pill while leaving this dialog open.
      onChanged?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to remove the payment');
    } finally {
      setRemovingId('');
    }
  };

  const submit = async () => {
    const linking = !!linkReceiptId;
    const amt = linking ? Number(selectedReceipt?.amount || 0) : Number(amount || 0);
    if (amt <= 0) { toast.error('Enter a payment amount greater than 0'); return; }
    if (!linking && outstanding > 0 && amt > outstanding + 0.01) {
      toast.error(`Amount exceeds the outstanding balance of ${fmtINR(outstanding)}`);
      return;
    }
    setSaving(true);
    try {
      const payload = linking
        ? { amount: amt, method, date, note, receiptId: linkReceiptId }
        : { amount: amt, method, date, note, accountId: accountId || null };
      const res = await axios.post(
        `${BACKEND_URL}/api/invoices/${encodeURIComponent(invoice.invoiceNumber)}/payment`,
        payload,
        { withCredentials: true },
      );
      const saved = res.data || {};
      // One-tap WhatsApp receipt to the traveller (if a phone is on file).
      const phone = String((invoice.client && invoice.client.phone) || '').replace(/\D/g, '');
      const newDue = Math.max(0, Number(saved.totalAmount || total) - Number(saved.amountPaid || 0));
      const clientName = (invoice.client && invoice.client.name) || 'there';
      const msg = `Hi ${clientName}, we've received your payment of ${fmtINR(amt)} towards invoice ${invoice.invoiceNumber}.`
        + (newDue > 0 ? ` Balance due: ${fmtINR(newDue)}.` : ' Your invoice is now fully paid — thank you!')
        + ' — Travcations';
      if (phone) {
        toast.success(`Payment of ${fmtINR(amt)} recorded`, {
          action: {
            label: 'WhatsApp receipt',
            onClick: () => window.open(`https://wa.me/${phone}?text=${encodeURIComponent(msg)}`, '_blank'),
          },
        });
      } else {
        toast.success(`Payment of ${fmtINR(amt)} recorded`);
      }
      onRecorded?.(saved);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to record payment');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-md" data-testid="record-payment-dialog">
        <DialogHeader>
          <DialogTitle>Record payment</DialogTitle>
        </DialogHeader>
        {invoice ? (
          <div className="space-y-3 mt-1">
            <div className="flex items-center justify-between text-xs bg-slate-50 border border-slate-200 rounded-lg px-3 py-2">
              <span className="font-mono font-semibold text-slate-800">{invoice.invoiceNumber}</span>
              <span className="text-slate-500">Outstanding <span className="font-semibold text-rose-700" data-testid="payment-outstanding">{fmtINR(outstanding)}</span></span>
            </div>
            {/* Payments already recorded, with an admin/accounts-only Remove per
                row — wires up the existing DELETE endpoint so a wrongly-entered
                payment can be reversed without a direct DB edit. */}
            {payments.length > 0 ? (
              <div className="border border-slate-200 rounded-lg" data-testid="payment-existing-wrap">
                <div className="flex items-center justify-between px-3 py-1.5 border-b border-slate-100">
                  <span className="text-[10px] uppercase tracking-wide text-slate-500">Recorded payments</span>
                  <span className="text-[10px] text-slate-400" data-testid="payment-existing-count">{payments.length}</span>
                </div>
                <div className="divide-y divide-slate-100 max-h-40 overflow-y-auto">
                  {payments.map((p) => (
                    <PaymentRow
                      key={p.paymentId}
                      payment={p}
                      canRemove={canRemovePayment}
                      removing={removingId === p.paymentId}
                      onRemove={handleRemovePayment}
                    />
                  ))}
                </div>
              </div>
            ) : null}
            {/* BATCH 2 dedupe guard — settle an existing trip receipt (counts once). */}
            {invoice.tripId && receipts.length > 0 && (
              <div data-testid="payment-link-receipt-wrap">
                <Label className="text-xs">Settle an existing trip receipt <span className="text-slate-400">(optional — avoids double-counting)</span></Label>
                <Select value={linkReceiptId || 'none'} onValueChange={(v) => setLinkReceiptId(v === 'none' ? '' : v)}>
                  <SelectTrigger className="mt-1" data-testid="payment-link-receipt"><SelectValue placeholder="New money (not from a receipt)" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">New money (not from a receipt)</SelectItem>
                    {receipts.map((r) => (
                      <SelectItem key={r.receiptId} value={r.receiptId}>
                        {fmtINR(r.amount)} · {(r.method || 'bank')}{r.date ? ` · ${r.date}` : ''}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {selectedReceipt && (
                  <p className="text-[11px] text-teal-700 mt-1" data-testid="payment-link-hint">
                    Linking receipt {fmtINR(selectedReceipt.amount)} — recorded once, no new money added.
                  </p>
                )}
              </div>
            )}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Amount ₹ *</Label>
                <Input type="number" min="0" value={linkReceiptId ? String(selectedReceipt?.amount ?? '') : amount} onChange={(e) => setAmount(e.target.value)} disabled={!!linkReceiptId} data-testid="payment-amount" className="mt-1" />
              </div>
              <div>
                <Label className="text-xs">Date</Label>
                <Input type="date" value={date} onChange={(e) => setDate(e.target.value)} data-testid="payment-date" className="mt-1" />
              </div>
            </div>
            <div>
              <Label className="text-xs">Method</Label>
              <Select value={method} onValueChange={setMethod}>
                <SelectTrigger className="mt-1" data-testid="payment-method"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="cash">Cash</SelectItem>
                  <SelectItem value="upi">UPI</SelectItem>
                  <SelectItem value="bank">Bank transfer</SelectItem>
                  <SelectItem value="cheque">Cheque</SelectItem>
                  <SelectItem value="card">Card</SelectItem>
                  <SelectItem value="other">Other</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Note (optional)</Label>
              <Input value={note} onChange={(e) => setNote(e.target.value)} placeholder="e.g. UPI ref, cheque no…" data-testid="payment-note" className="mt-1" />
            </div>
            {!linkReceiptId && (
              <div>
                <Label className="text-xs">Account (optional)</Label>
                <Select value={accountId || 'none'} onValueChange={(v) => setAccountId(v === 'none' ? '' : v)}>
                  <SelectTrigger className="mt-1" data-testid="payment-account-select"><SelectValue placeholder="Unassigned" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Unassigned</SelectItem>
                    {bankAccounts.map((a) => (
                      <SelectItem key={a.accountId} value={a.accountId}>{a.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>
        ) : null}
        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid="payment-cancel-btn">Cancel</Button>
          <Button onClick={submit} disabled={saving} className="bg-emerald-700 hover:bg-emerald-800 text-white" data-testid="payment-save-btn">
            {saving ? 'Recording…' : 'Record payment'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const TotalsRow = ({ totals }) => (
  <div className="flex items-center justify-end gap-6 text-xs px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg" data-testid="invoices-totals">
    <span><span className="text-slate-500">Taxable</span> <span className="font-mono font-semibold ml-1">{fmtINR(totals.taxable)}</span></span>
    <span><span className="text-slate-500">GST</span> <span className="font-mono font-semibold ml-1 text-amber-700">{fmtINR(totals.gst)}</span></span>
    <span><span className="text-slate-500">TCS</span> <span className="font-mono font-semibold ml-1 text-amber-700">{fmtINR(totals.tcs)}</span></span>
    <span><span className="text-slate-500">Total</span> <span className="font-mono font-bold ml-1 text-slate-900">{fmtINR(totals.total)}</span></span>
  </div>
);

export default InvoicesList;
